import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import axios from "axios";
import { baseUrl } from "../../constants";
import { useParams, useLocation } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Fab,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Zoom,
  CircularProgress,
  Menu,
  MenuItem,
  Popover,
  Snackbar,
  Alert,
  
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import SendIcon from "@mui/icons-material/Send";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ReplyIcon from "@mui/icons-material/Reply";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import AddReactionIcon from "@mui/icons-material/AddReaction";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import dayjs from "dayjs";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { useChatSocket } from "./ChatSocketProvider";

/* ----------------------------- Types ----------------------------- */
type Author = {
  id: number;
  username: string;
  profile?: { name?: string | null; imageUrl?: string | null } | null;
};
type ReplyPreview = {
  id: number;
  content: string;
  deletedAt?: string | null;
  author: Author;
};
type ReactionChip = { emoji: string; count: number; reactedByMe: boolean };

type Message = {
  id: number;
  channelId: number;
  content: string;
  replyToId: number | null;
  editedAt: string | null;
  deletedAt?: string | null;
  createdAt: string;
  author: Author;
  replyTo?: ReplyPreview | null;
  reactions?: ReactionChip[];
};

type ListResponse = { items: Message[]; nextCursor: number | null };

/* --------------------------- Constants --------------------------- */
const TOP_LOAD_THRESHOLD = 120;
const SCROLL_LOAD_COOLDOWN = 350;
const TYPING_DEBOUNCE_MS = 300;
const TYPING_HIDE_AFTER_MS = 2500;

/* --------------------------- Component --------------------------- */

export default function ChatWindow({ currentUserId }: { currentUserId: number }) {
  const theme = useTheme();
  const { channelId } = useParams();
  const chId = Number(channelId);
  const { socket } = useChatSocket();

  const [messages, setMessages] = useState<Message[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [channelName, setChannelName] = useState<string>("Channel");
  const [atBottom, setAtBottom] = useState(true);

  // typing indicator
  const [typingUsers, setTypingUsers] = useState<Record<number, number>>({});
  const typingTimerRef = useRef<number | null>(null);
  const lastTypingEmitRef = useRef<number>(0);

  // inline edit/delete
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuMsgId, setMenuMsgId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState<string>("");

  // reply
  const [replyTo, setReplyTo] = useState<Message | null>(null);

  // emoji composer
  const [emojiAnchor, setEmojiAnchor] = useState<HTMLElement | null>(null);

  // add reaction popover
  const [reactionAnchor, setReactionAnchor] = useState<HTMLElement | null>(null);
  const [reactionForMsg, setReactionForMsg] = useState<number | null>(null);

  // header menu (channel options)
  const [hdrAnchor, setHdrAnchor] = useState<null | HTMLElement>(null);

  // toast
  const [toast, setToast] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error" | "info";
  }>({ open: false, msg: "", severity: "info" });

  const token = useMemo(
    () => localStorage.getItem("accessToken") || localStorage.getItem("token"),
    []
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const initialAutoScrolledRef = useRef(false);
  const lastLoadTickRef = useRef(0);
  const lastRequestedCursorRef = useRef<number | null>(null);

  // deep-link param
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const jumpMid = Number(params.get("mid") || 0);

  /* ------------------------ Channel header ----------------------- */
  useEffect(() => {
    if (!chId) return;
    const t = localStorage.getItem("accessToken") || localStorage.getItem("token");
    axios
      .get<{ id: number; name: string }[]>(`${baseUrl}/chat/channels`, {
        headers: { Authorization: `Bearer ${t}` },
      })
      .then((res) => {
        const ch = (res.data || []).find((c) => c.id === chId);
        setChannelName(ch?.name ?? "Channel");
      })
      .catch(() => setChannelName("Channel"));
  }, [chId]);

  /* ------------------------ Initial load + sockets ------------------------ */
  useEffect(() => {
    if (!chId) return;

    setMessages([]);
    setNextCursor(null);
    setHasMore(false);
    setLoading(true);
    initialAutoScrolledRef.current = false;
    lastRequestedCursorRef.current = null;
    setTypingUsers({});
    setReplyTo(null);

    axios
      .get<ListResponse>(`${baseUrl}/chat/channels/${chId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 30 },
      })
      .then((res) => {
        setMessages(res.data.items);
        setNextCursor(res.data.nextCursor);
        setHasMore(!!res.data.nextCursor);
        // mark read
        axios.post(
          `${baseUrl}/chat/channels/${chId}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        socket?.emit("read", { channelId: chId });

        requestAnimationFrame(() => {
          bottomRef.current?.scrollIntoView();
          initialAutoScrolledRef.current = true;
        });
      })
      .finally(() => setLoading(false));

    if (!socket) return;

    socket.emit("join", { channelId: chId });

    const onCreated = (msg: Message) => {
      if (msg.channelId !== chId) return;
      setMessages((prev) => [...prev, msg]);
      socket?.emit("read", { channelId: chId });
      if (isAtBottom(containerRef.current)) {
        requestAnimationFrame(() =>
          bottomRef.current?.scrollIntoView({ behavior: "smooth" })
        );
      }
    };

    const onUpdated = (msg: Message) => {
      if (msg.channelId !== chId) return;
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === msg.id);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = { ...prev[idx], ...msg };
        return next;
      });
    };

    const onDeleted = (msg: Message) => {
      if (msg.channelId !== chId) return;
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === msg.id);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = { ...prev[idx], ...msg };
        return next;
      });
    };

    const onReaction = (p: {
      messageId: number;
      channelId: number;
      reactions: ReactionChip[];
    }) => {
      if (p.channelId !== chId) return;
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === p.messageId);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], reactions: p.reactions };
        return next;
      });
    };

    const onTyping = (p: { userId: number; isTyping: boolean }) => {
      if (p.userId === currentUserId) return;
      setTypingUsers((prev) => {
        const copy = { ...prev };
        if (p.isTyping) copy[p.userId] = Date.now() + TYPING_HIDE_AFTER_MS;
        else delete copy[p.userId];
        return copy;
      });
    };

    socket.on("message.created", onCreated);
    socket.on("message.updated", onUpdated);
    socket.on("message.deleted", onDeleted);
    socket.on("reaction.updated", onReaction);
    socket.on("user.typing", onTyping);

    return () => {
      socket.off("message.created", onCreated);
      socket.off("message.updated", onUpdated);
      socket.off("message.deleted", onDeleted);
      socket.off("reaction.updated", onReaction);
      socket.off("user.typing", onTyping);
      socket.emit("leave", { channelId: chId });
    };
  }, [chId, socket, token, currentUserId]);

  /* ----------------- Deep-link to specific message ---------------- */
  useEffect(() => {
    const handoffRaw = sessionStorage.getItem("chat:jump");
    const handoff = handoffRaw
      ? (JSON.parse(handoffRaw) as { channelId: number; messageId: number })
      : null;
    const targetId = jumpMid || handoff?.messageId || 0;

    if (!chId || !targetId) return;
    if (handoff && handoff.channelId !== chId) return;

    axios
      .get(`${baseUrl}/chat/messages/${targetId}/context`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { before: 25, after: 25 },
      })
      .then((res) => {
        const { channelId, items, nextCursor /* prevCursor */ } = res.data || {};
        if (channelId !== chId || !Array.isArray(items)) return;
        setMessages(items);
        setNextCursor(nextCursor ?? null);
        setHasMore(!!nextCursor);
        requestAnimationFrame(() => {
          document
            .getElementById(`msg-${targetId}`)
            ?.scrollIntoView({ block: "center" });
        });
      })
      .catch(() => {
        requestAnimationFrame(() => bottomRef.current?.scrollIntoView());
      })
      .finally(() => {
        sessionStorage.removeItem("chat:jump");
      });
  }, [chId, jumpMid, token]);

  /* ----------------------- Typing expiry loop ----------------------- */
  useEffect(() => {
    const id = window.setInterval(() => {
      setTypingUsers((prev) => {
        const now = Date.now();
        const copy: Record<number, number> = {};
        let changed = false;
        for (const [uidStr, expiry] of Object.entries(prev)) {
          const uid = Number(uidStr);
          if (expiry > now) copy[uid] = expiry;
          else changed = true;
        }
        return changed ? copy : prev;
      });
    }, 500);
    return () => clearInterval(id);
  }, []);

  /* ------------------------- Scroll handler ------------------------- */
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setAtBottom(isAtBottom(el));
    const now = Date.now();
    if (
      hasMore &&
      !loadingOlder &&
      now - lastLoadTickRef.current > SCROLL_LOAD_COOLDOWN &&
      el.scrollTop <= TOP_LOAD_THRESHOLD
    ) {
      lastLoadTickRef.current = now;
      void loadOlder();
    }
  }, [hasMore, loadingOlder]);

  /* ---------------------------- Load older ---------------------------- */
  const loadOlder = useCallback(async () => {
    if (!hasMore || loadingOlder || !nextCursor) return;
    if (lastRequestedCursorRef.current === nextCursor) return;
    lastRequestedCursorRef.current = nextCursor;

    const el = containerRef.current;
    const prevHeight = el?.scrollHeight ?? 0;
    const prevTop = el?.scrollTop ?? 0;

    setLoadingOlder(true);
    try {
      const res = await axios.get<ListResponse>(
        `${baseUrl}/chat/channels/${chId}/messages`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { cursor: nextCursor, limit: 30 },
        }
      );
      setMessages((prev) => [...res.data.items, ...prev]);
      setNextCursor(res.data.nextCursor);
      setHasMore(!!res.data.nextCursor);

      requestAnimationFrame(() => {
        const newHeight = el?.scrollHeight ?? 0;
        if (el) el.scrollTop = prevTop + (newHeight - prevHeight);
      });
    } finally {
      setLoadingOlder(false);
    }
  }, [hasMore, loadingOlder, nextCursor, chId, token]);

  /* ---------------------------- Emit typing ---------------------------- */
  const emitTyping = useCallback(
    (isTyping: boolean) => {
      const now = Date.now();
      if (isTyping) {
        if (now - lastTypingEmitRef.current < TYPING_DEBOUNCE_MS) return;
        lastTypingEmitRef.current = now;
      }
      socket?.emit("typing", { channelId: chId, isTyping });
    },
    [socket, chId]
  );

  /* ------------------------------- Send ------------------------------- */
  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    const payload: any = { content: trimmed };
    if (replyTo) payload.replyToId = replyTo.id;

    setInput("");
    setReplyTo(null);
    emitTyping(false);

    const useSocket = socket && (socket.connected ?? true);
    if (useSocket) {
      try {
        socket!.emit("send-message", { channelId: chId, dto: payload });
        return;
      } catch {
        /* HTTP fallback below */
      }
    }
    const { data } = await axios.post<Message>(
      `${baseUrl}/chat/channels/${chId}/messages`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setMessages((prev) => [...prev, data]);
    requestAnimationFrame(() =>
      bottomRef.current?.scrollIntoView({ behavior: "smooth" })
    );
  }, [input, replyTo, emitTyping, socket, chId, token]);

  /* ----------------------- Per-message menu ----------------------- */
  const openMenu = (e: React.MouseEvent<HTMLElement>, msgId: number) => {
    setMenuAnchor(e.currentTarget);
    setMenuMsgId(msgId);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuMsgId(null);
  };

  const startEdit = (msg: Message) => {
    setEditingId(msg.id);
    setEditingText(msg.content);
    closeMenu();
  };
  const saveEdit = async () => {
    if (!editingId) return;
    const trimmed = editingText.trim();
    if (!trimmed) {
      cancelEdit();
      return;
    }
    try {
      socket?.emit("message.edit", { messageId: editingId, content: trimmed });
    } catch {
      await axios.post(
        `${baseUrl}/chat/messages/${editingId}/edit`,
        { content: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } finally {
      setEditingId(null);
      setEditingText("");
    }
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const deleteMsg = async (id: number) => {
    closeMenu();
    const ok = window.confirm("Delete this message?");
    if (!ok) return;
    try {
      socket?.emit("message.delete", { messageId: id });
    } catch {
      await axios.post(
        `${baseUrl}/chat/messages/${id}/delete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }
  };

  const copyMsg = async (msg: Message) => {
    try {
      await navigator.clipboard.writeText(msg.content || "");
      setToast({ open: true, msg: "Copied", severity: "success" });
    } catch {
      setToast({ open: true, msg: "Copy failed", severity: "error" });
    }
    closeMenu();
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
    emitTyping(true);
    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    typingTimerRef.current = window.setTimeout(
      () => emitTyping(false),
      TYPING_HIDE_AFTER_MS
    ) as any;
  };

  // composer emoji
  const openEmoji = (e: React.MouseEvent<HTMLElement>) =>
    setEmojiAnchor(e.currentTarget as HTMLElement);
  const closeEmoji = () => setEmojiAnchor(null);
  const onEmojiSelect = (emoji: any) => {
    const toAdd = emoji?.native ?? emoji?.shortcodes ?? "";
    setInput((prev) => `${prev}${toAdd}`);
  };

  // reply
  const beginReply = (msg: Message) => setReplyTo(msg);
  const cancelReplyPreview = () => setReplyTo(null);

  // header menu
  const openHdrMenu = (e: React.MouseEvent<HTMLElement>) =>
    setHdrAnchor(e.currentTarget);
  const closeHdrMenu = () => setHdrAnchor(null);

  const purgeChannel = async () => {
    closeHdrMenu();
    const ok = window.confirm(
      "Delete ALL messages in this chat? This cannot be undone."
    );
    if (!ok) return;
    try {
      await axios.post(
        `${baseUrl}/chat/channels/${chId}/purge`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages([]);
      setNextCursor(null);
      setHasMore(false);
      setToast({ open: true, msg: "Chat cleared", severity: "success" });
      socket?.emit("read", { channelId: chId });
    } catch (e: any) {
      setToast({
        open: true,
        msg: e?.response?.data?.message || "Purge endpoint not available",
        severity: "error",
      });
    }
  };

  // reactions
  const toggleReaction = (m: Message, emoji: string) => {
    const already = (m.reactions || []).find(
      (r) => r.emoji === emoji && r.reactedByMe
    );
    try {
      if (already) {
        socket?.emit("reaction.remove", { messageId: m.id, emoji });
      } else {
        socket?.emit("reaction.add", { messageId: m.id, emoji });
      }
    } catch {
      // HTTP fallback
      if (already) {
        axios.delete(
          `${baseUrl}/chat/messages/${m.id}/reactions/${encodeURIComponent(
            emoji
          )}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        axios.post(
          `${baseUrl}/chat/messages/${m.id}/reactions`,
          { emoji },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    }
  };

  const openAddReaction = (e: React.MouseEvent<HTMLElement>, msgId: number) => {
    setReactionAnchor(e.currentTarget);
    setReactionForMsg(msgId);
  };
  const closeAddReaction = () => {
    setReactionAnchor(null);
    setReactionForMsg(null);
  };
  const onSelectReaction = (emojiObj: any) => {
    const emoji = emojiObj?.native ?? emojiObj?.shortcodes ?? "";
    if (emoji && reactionForMsg) {
      const m = messages.find((x) => x.id === reactionForMsg);
      if (m) toggleReaction(m, emoji);
    }
    closeAddReaction();
  };

  // day dividers
  const withDividers: (Message | { __divider: string; key: string })[] =
    useMemo(() => {
      const out: (Message | { __divider: string; key: string })[] = [];
      let lastDay: string | null = null;
      messages.forEach((m) => {
        const day = dayjs(m.createdAt).format("YYYY-MM-DD");
        if (day !== lastDay) {
          out.push({ __divider: day, key: `div-${day}-${m.id}` });
          lastDay = day;
        }
        out.push(m);
      });
      return out;
    }, [messages]);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          bgcolor: "background.paper",
          position: "sticky",
          top: 0,
          zIndex: 1,
        }}
      >
        <Typography variant="subtitle1" noWrap fontWeight={700}>
          {channelName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TypingBar typingUsers={typingUsers} currentUserId={currentUserId} />
          {hasMore && (
            <Button
              size="small"
              onClick={loadOlder}
              disabled={loadingOlder}
              variant="outlined"
            >
              {loadingOlder ? "Loading…" : "Load older"}
            </Button>
          )}
          <Tooltip title="Channel options">
            <IconButton onClick={openHdrMenu} size="small" aria-label="Channel options">
              <SettingsOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={hdrAnchor} open={!!hdrAnchor} onClose={closeHdrMenu}>
            <MenuItem onClick={purgeChannel} sx={{ color: "error.main" }}>
              <DeleteSweepIcon fontSize="small" style={{ marginRight: 8 }} />
              Delete chat
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Messages */}
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        sx={{
          p: 2,
          flex: 1,
          overflowY: "auto",
          position: "relative",
          background:
            theme.palette.mode === "light"
              ? theme.palette.grey[50]
              : theme.palette.background.default,
        }}
      >
        {loadingOlder && (
          <Box sx={{ py: 1, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={16} />
          </Box>
        )}

        {loading && !messages.length ? (
          <Typography color="text.secondary">Loading messages…</Typography>
        ) : (
          <Stack spacing={1.25}>
            {withDividers.map((it) =>
              isDivider(it) ? (
                <DayDivider key={it.key} day={it.__divider} />
              ) : (
                <MessageRow
                  key={it.id}
                  m={it}
                  me={it.author.id === currentUserId}
                  onMenu={(e) => openMenu(e, it.id)}
                  onReply={() => beginReply(it)}
                  onToggleReaction={toggleReaction}
                  onOpenAddReaction={(e) => openAddReaction(e, it.id)}
                />
              )
            )}
            <div ref={bottomRef} />
          </Stack>
        )}

        {/* Scroll-to-bottom FAB */}
        <Zoom in={!atBottom}>
          <Fab
            size="small"
            color="primary"
            onClick={() =>
              bottomRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            sx={{ position: "absolute", right: 16, bottom: 16 }}
            aria-label="Scroll to latest"
          >
            <KeyboardArrowDownIcon />
          </Fab>
        </Zoom>
      </Box>

      <Divider />

      {/* Composer */}
      <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        {replyTo && (
          <Box
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: (t) => `1px dashed ${t.palette.divider}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <ReplyIcon fontSize="small" />
              <Typography variant="caption" sx={{ opacity: 0.8 }} noWrap>
                Replying to{" "}
                <b>{replyTo.author.profile?.name || replyTo.author.username}</b>
                :&nbsp;
                <i style={{ opacity: 0.8 }}>{truncate(replyTo.content, 80)}</i>
              </Typography>
            </Box>
            <IconButton size="small" onClick={cancelReplyPreview} aria-label="Cancel reply">
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
          <Tooltip title="Emoji">
            <IconButton onClick={openEmoji} size="small" aria-label="Insert emoji">
              <EmojiEmotionsOutlinedIcon />
            </IconButton>
          </Tooltip>

          <TextField
            value={input}
            onChange={onInputChange}
            placeholder="Write a message…"
            fullWidth
            size="small"
            multiline
            maxRows={6}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                handleSend();
              } else if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              } else if (e.key === "Escape") {
                if (replyTo) cancelReplyPreview();
              }
            }}
            onBlur={() => emitTyping(false)}
          />

          <Tooltip title="Send (Enter)">
            <IconButton onClick={handleSend} aria-label="Send message" color="primary">
              <SendIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Message action menu */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            const msg = messages.find((m) => m.id === menuMsgId);
            if (msg) startEdit(msg);
          }}
          disabled={
            !menuMsgId ||
            messages.find((m) => m.id === menuMsgId)?.author.id !== currentUserId
          }
        >
          <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => menuMsgId && deleteMsg(menuMsgId)}
          disabled={
            !menuMsgId ||
            messages.find((m) => m.id === menuMsgId)?.author.id !== currentUserId
          }
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" style={{ marginRight: 8 }} /> Delete
        </MenuItem>
        <MenuItem
          onClick={() => {
            const msg = messages.find((m) => m.id === menuMsgId);
            if (msg) copyMsg(msg);
          }}
          disabled={!menuMsgId}
        >
          <ContentCopyIcon fontSize="small" style={{ marginRight: 8 }} /> Copy
        </MenuItem>
        <MenuItem
          onClick={() => {
            const msg = messages.find((m) => m.id === menuMsgId);
            if (msg) setReplyTo(msg);
            closeMenu();
          }}
          disabled={!menuMsgId}
        >
          <ReplyIcon fontSize="small" style={{ marginRight: 8 }} /> Reply
        </MenuItem>
      </Menu>

      {/* Emoji picker (composer) */}
      <Popover
        open={!!emojiAnchor}
        anchorEl={emojiAnchor}
        onClose={closeEmoji}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { boxShadow: 3 } }}
      >
        <Box sx={{ p: 1 }}>
          <Picker data={data} onEmojiSelect={onEmojiSelect} theme="auto" />
        </Box>
      </Popover>

      {/* Add reaction picker */}
      <Popover
        open={!!reactionAnchor}
        anchorEl={reactionAnchor}
        onClose={closeAddReaction}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{ sx: { boxShadow: 3 } }}
      >
        <Box sx={{ p: 1 }}>
          <Picker data={data} onEmojiSelect={onSelectReaction} theme="auto" />
        </Box>
      </Popover>

      {/* Inline edit overlay */}
      {editingId && (
        <EditOverlay
          value={editingText}
          onChange={setEditingText}
          onSave={saveEdit}
          onCancel={cancelEdit}
        />
      )}

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((p) => ({ ...p, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

/* ====================== Small components ====================== */

function TypingBar({
  typingUsers,
  currentUserId,
}: {
  typingUsers: Record<number, number>;
  currentUserId: number;
}) {
  const ids = Object.keys(typingUsers)
    .map((s) => Number(s))
    .filter((id) => id !== currentUserId);
  if (!ids.length) return null;
  const label = ids.length === 1 ? "is typing…" : "are typing…";
  return (
    <Typography variant="caption" color="text.secondary" role="status" aria-live="polite">
      {ids.length} {label}
    </Typography>
  );
}

function DayDivider({ day }: { day: string }) {
  const d = dayjs(day);
  const text = d.isSame(dayjs(), "day")
    ? "Today"
    : d.isSame(dayjs().subtract(1, "day"), "day")
    ? "Yesterday"
    : d.format("MMM D, YYYY");

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 1 }}>
      <Divider sx={{ flex: 1 }} />
      <Chip size="small" label={text} />
      <Divider sx={{ flex: 1 }} />
    </Box>
  );
}

const MessageRow = memo(function MessageRow({
  m,
  me,
  onMenu,
  onReply,
  onToggleReaction,
  onOpenAddReaction,
}: {
  m: Message;
  me: boolean;
  onMenu: (e: React.MouseEvent<HTMLElement>) => void;
  onReply: () => void;
  onToggleReaction: (m: Message, emoji: string) => void;
  onOpenAddReaction: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  const displayName = m.author.profile?.name || m.author.username;
  const avatar = m.author.profile?.imageUrl || undefined;
  const isDeleted = !!m.deletedAt;
  const reactions = m.reactions || [];

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.25,
        justifyContent: me ? "flex-end" : "flex-start",
      }}
    >
      {!me && (
        <Avatar src={avatar} alt={displayName} sx={{ width: 28, height: 28 }} />
      )}

      {/* Anchor for deep-linking / scroll-to-message */}
      <Box
        id={`msg-${m.id}`}
        data-msg-id={m.id}
        tabIndex={-1}
        className="msg-wrapper"
        sx={{
          maxWidth: "72%",
          p: 1,
          borderRadius: 2,
          bgcolor: me ? "primary.main" : "background.paper",
          color: me ? "primary.contrastText" : "text.primary",
          boxShadow: 1,
          opacity: isDeleted ? 0.7 : 1,
          position: "relative",
          transition: "background-color .2s",
          scrollMarginTop: "72px",
          "&:hover .msg-actions": { opacity: 1, pointerEvents: "auto" },
        }}
      >
        {/* header row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
          <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 600 }}>
            {displayName}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            • {dayjs(m.createdAt).format("MMM D, HH:mm")}
            {m.editedAt && !isDeleted ? " (edited)" : ""}
          </Typography>

          <Box
            className="msg-actions"
            sx={{ ml: "auto", display: "flex", gap: 0.5, opacity: 0 }}
          >
            <Tooltip title="Add reaction">
              <IconButton
                size="small"
                onClick={onOpenAddReaction}
                sx={{ opacity: 0.9 }}
                aria-label="Add reaction"
              >
                <AddReactionIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reply">
              <IconButton size="small" onClick={onReply} sx={{ opacity: 0.9 }} aria-label="Reply">
                <ReplyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title="More">
              <IconButton size="small" onClick={onMenu} sx={{ opacity: 0.9 }} aria-label="More">
                <MoreVertIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* quoted reply preview */}
        {m.replyTo && (
          <Box
            sx={{
              mb: 0.5,
              p: 0.75,
              borderRadius: 1,
              bgcolor: me ? "rgba(255,255,255,.14)" : "action.hover",
              border: (t) => `1px dashed ${t.palette.divider}`,
            }}
          >
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Replying to{" "}
              <b>{m.replyTo.author.profile?.name || m.replyTo.author.username}</b>
              {m.replyTo.deletedAt ? " • (deleted)" : ""}
            </Typography>
            {!m.replyTo.deletedAt && (
              <Typography variant="body2" sx={{ opacity: 0.95 }} noWrap>
                {truncate(m.replyTo.content, 120)}
              </Typography>
            )}
          </Box>
        )}

        {/* content */}
        <Typography
          variant="body2"
          whiteSpace="pre-wrap"
          sx={{ textDecoration: isDeleted ? "line-through" : "none" }}
        >
          {m.content}
        </Typography>

        {/* reactions */}
        {!!reactions.length && (
          <Box sx={{ mt: 0.5, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {reactions.map((r) => (
              <Chip
                key={r.emoji}
                size="small"
                label={`${r.emoji} ${r.count}`}
                variant={r.reactedByMe ? "filled" : "outlined"}
                onClick={() => onToggleReaction(m, r.emoji)}
                sx={{ height: 24, "& .MuiChip-label": { px: 1 } }}
              />
            ))}
          </Box>
        )}
      </Box>

      {me && (
        <Avatar src={avatar} alt={displayName} sx={{ width: 28, height: 28 }} />
      )}
    </Box>
  );
});

function EditOverlay({
  value,
  onChange,
  onSave,
  onCancel,
}: {
  value: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        bgcolor: "rgba(0,0,0,.2)",
        display: "grid",
        placeItems: "center",
        zIndex: (t) => t.zIndex.modal + 1,
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
    >
      <Paper
        sx={{
          width: "min(720px, 92vw)",
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Edit message
        </Typography>
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          multiline
          maxRows={8}
          autoFocus
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
              e.preventDefault();
              onSave();
            }
          }}
        />
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button onClick={onCancel} variant="text">
            Cancel
          </Button>
          <Button onClick={onSave} variant="contained">
            Save
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

/* ----------------------------- helpers ----------------------------- */
function isAtBottom(el: HTMLDivElement | null) {
  if (!el) return true;
  const threshold = 20;
  return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
}
function isDivider(it: any): it is { __divider: string; key: string } {
  return !!(it && (it as any).__divider);
}
function truncate(s: string, n = 80) {
  if (!s) return s;
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}
